import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Database, Loader2, Trash2 } from 'lucide-react';
import { AddCategory, CategoryFormModal } from './CatalogCategoryControls';
import { CatalogItemModal, type CatalogItemFields } from '../CatalogItemModal';
import { CatalogCategoryGroup, groupByCategory } from './CatalogCategoryGroup';
import { photoSrc, schematicSrc } from './CatalogItemRow';
import { CatalogImageModal } from './CatalogImageModal';
import { NoticeProductImages, listingImageSrc, type NoticePhotos } from './NoticeProductImages';
import { ProductModelsTable } from './ProductModelsTable';
import { ConfirmDialog } from '../ConfirmDialog';
import { SectionNav } from '../SectionNav';
import { CenteredSelect } from '../CenteredSelect';
import { displayVariantLabel } from '../variantLabel';
import { GEN_MODELS } from '../models';
import { invalidateCatalogItems } from '../catalogItems';
import { invalidateCategories, localizeCategory } from '../catalogCategories';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useStableText } from '../../../components/StableText';
import { MeshModal } from '../MeshModal';
import {
  fetchCatalogModels, meshSrc, meshStamp, type Job3D, isTrueScale,
} from '../threeDApi';
import type { CatalogItem, Photo, Schematic } from './CatalogItemRow';
import { useScrollMemory } from '../scrollMemory';
import { IS_DEMO } from '../../../apiBase';

// An ontology category: a folder name plus a definition the LLMs use to classify
// parts (empty for legacy categories that only exist on items). `parent` is the
// super-category it sits inside, '' for a top level — two levels, no deeper.
type Category = { name: string; nameFr: string; definition: string; parent: string };

// Parts database: the persistent, reusable catalog of parts & tools, shown as a
// bordered table grouped into category folders. Items are recategorized by
// dragging a row onto another folder; new categories are created here via the
// "Add category" control (the one place that edits the ontology). Other item
// fields are added via the modal.
export function NoticeCatalog() {
  // The database is one long table, and it is a page you come back to. Per route
  // rather than per row: there is one of it.
  useScrollMemory('catalog');
  const { t, language } = useLanguage();
  const st = useStableText();
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  // The table stays hidden behind the spinner until every schematic and photo has been
  // preloaded, so it appears whole instead of jittering as pictures trickle in and
  // resize their rows. Flipped once, on the first load — later edits keep the table up
  // (their pictures are already cached), they do not flash it back to a spinner.
  const [ready, setReady] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  // The finished-product photos, grouped by notice (owned here so the notice filter can
  // draw its options from both tables and drive both).
  const [productNotices, setProductNotices] = useState<NoticePhotos[]>([]);
  // The notice both tables are filtered to (its doc id), or '' for all notices.
  const [noticeFilter, setNoticeFilter] = useState('');
  // And which of that notice's submodels (its id), or '' for all of them. A generated
  // part photo belongs to one colour; a screw's does not.
  const [submodelFilter, setSubmodelFilter] = useState('');
  // The folders that are shut. Expanded is the default — the catalog's whole job is to
  // show you what is in it — and this is not persisted: a folder you shut to read
  // another one is not a preference, it is a gesture, and it should not still be shut
  // tomorrow.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapsed = (value: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(value)) next.add(value);
      return next;
    });

  // ?item=<id>: the row a parts table sent us to, so a "TROUVÉ EN BASE — OUI" can be
  // checked. The table's claim is that a part IS this row; this is where that is
  // either obviously right or obviously wrong.
  const [params] = useSearchParams();
  const linkedId = Number(params.get('item')) || null;
  // ?product=<docId>&submodel=<id>: the whole product's row, from the same column of a parts
  // table — on this first tab, over the parts (see ProductModelsTable).
  const linkedProduct = params.get('product')
    ? { docId: params.get('product') as string, submodel: params.get('submodel') || '' }
    : null;
  const linkedRef = useRef<HTMLTableRowElement>(null);
  // ?from=<path>: the page that sent us, to go back to exactly.
  //
  // Read as a path and never as a URL. This value comes from the address bar, and it is
  // handed to a link — which is how a page ends up bouncing people off the site. One
  // leading slash, and no second one: "//evil.com" is a URL, not a path. Backslashes
  // are refused for the same reason a second slash is: browsers fold "/\evil.com" into
  // exactly that.
  const backTo = (() => {
    const raw = params.get('from') || '';
    return /^\/(?!\/)[^\s\\]*$/.test(raw) ? raw : null;
  })();

  // Which of the two databases is on screen. They answer different questions — what
  // parts exist, what the listings look like — and stacking them made the page long
  // enough that the second was mostly scrolled past. Opens on the first tab, EXCEPT
  // when ?item= names a part: that link points into the parts table, so it opens there.
  // Opens on the parts database: it is the first card now, and landing on the second
  // one would read as the page having lost track of which is which.
  const [db, setDb] = useState<'parts' | 'photos'>('parts');

  // Scroll to it once it exists — after the fetch, not on mount: the row is not
  // rendered until the items are in, and scrolling to nothing is silence, which reads
  // exactly like a broken link.
  //
  // Opening its folder first, for the same reason. Folders start open, so this only
  // matters when the link is followed from this very page with that folder shut — the
  // component never unmounts, the fold survives the navigation, and the row being
  // pointed at would not be rendered at all.
  useEffect(() => {
    if (!linkedId || loading) return;
    const cat = (items.find((i) => i.id === linkedId)?.category || '').trim();
    setCollapsed((prev) => {
      if (!prev.has(cat)) return prev;
      const next = new Set(prev);
      next.delete(cat);
      return next;
    });
  }, [linkedId, loading, items]);

  // Scroll to the ?item= row ONCE, after the effect above has opened its folder so the
  // row exists to scroll to. `collapsed` stays a dep because that open is what renders
  // the row — but the guard means folding other folders afterwards never yanks the
  // viewport back here.
  const scrolledTo = useRef<number | null>(null);
  useEffect(() => {
    if (!linkedId || loading || scrolledTo.current === linkedId) return;
    const el = linkedRef.current;
    if (!el) return; // row not rendered yet; a later collapsed change re-runs this
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    scrolledTo.current = linkedId;
  }, [linkedId, loading, collapsed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, ont, pics] = await Promise.all([
          axios.get<{ items: CatalogItem[] }>('/api/notice/catalog'),
          axios.get<{ categories: Category[] }>('/api/notice/categories'),
          axios.get<{ notices: NoticePhotos[] }>('/api/notice/listing-images'),
        ]);
        if (cancelled) return;
        setItems(cat.data.items || []);
        setCategories(ont.data.categories || []);
        setProductNotices(pics.data.notices || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Preload every picture of BOTH tables — the parts DB (schematics + photos) and the
  // product photos — then reveal them together. Runs once (the `ready` guard makes it a
  // no-op after the first reveal), so a recategorize or an edit never blanks the tables
  // — their pictures are already cached. An empty catalog resolves Promise.all([]) at once.
  useEffect(() => {
    if (loading || ready) return;
    let cancelled = false;
    const urls = [
      ...items.flatMap((it) => [
        ...it.schematics.map((s) => schematicSrc(s.id)),
        ...it.photos.map((p) => photoSrc(p.id)),
      ]),
      ...productNotices.flatMap((n) => n.images.map((im) => listingImageSrc(im.id))),
    ];
    Promise.all(
      urls.map(
        (u) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve(); // a broken picture must not wedge the table shut
            img.src = u;
          }),
      ),
    ).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loading, items, productNotices, ready]);

  // Every cell carries its own border, and the table its outer border, so the grid
  // reads as inner + outer lines.
  const cellCls = 'border border-slate-300 px-4 py-2 text-center align-middle';
  const headCls = `${cellCls} bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600`;

  // One name column: the language being read. Both were shown side by side, which
  // asked you to read a translation you did not ask for, on every row, to find the one
  // you did. The other name is not lost — it is what the other toggle shows, and both
  // are edited together in the item's own modal.
  const nameCols = [
    {
      label: t('notice.catalog.partName'),
      head: st('notice.catalog.partName'),
      get: (it: CatalogItem) => (language === 'fr' ? it.nameFr : it.nameEn),
    },
  ];

  // id + name + schematic + real images + 3D + the pencil
  // id, size, schematics, photos, models, pencil, plus the name columns.
  const colCount = 6 + nameCols.length;

  // How many real images the database holds per generation model — the per-model
  // split that used to be two columns, now a summary above the table. Derived from
  // `items` (the whole catalog, not the notice-filtered view), so it re-counts on its
  // own whenever a photo is added or removed. Photos with no model (hand-uploaded /
  // pre-model) aren't attributed to a Nano Banana model, so they don't count here.
  const modelImageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const it of items) {
      for (const p of it.photos) {
        if (p.model) counts[p.model] = (counts[p.model] ?? 0) + 1;
      }
    }
    return counts;
  }, [items]);

  // The notices referenced by either table — a part's schematics/photos and the product
  // photos — as filter options, labelled by their slug (brand-category-product, the
  // "as usual" notice identity), falling back to the file name.
  const noticeOptions = useMemo(() => {
    const byId = new Map<string, { docId: string; label: string }>();
    const add = (docId: string | null, slug: string | null, name: string | null) => {
      if (docId && !byId.has(docId)) byId.set(docId, { docId, label: slug || name || docId });
    };
    for (const it of items) {
      for (const s of it.schematics) add(s.docId, s.slug, s.docName);
      for (const p of it.photos) add(p.docId, p.slug, p.docName);
    }
    for (const n of productNotices) add(n.docId, n.slug, n.docName);
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items, productNotices]);

  // The submodels (colour/material variants) present in what is on screen, as filter
  // options. Read off the STORED rows rather than a notice's answers, because that is
  // what this page knows: a submodel exists here when something in the database is filed
  // under it. Keyed by the submodel id, labelled with what was saved alongside it — and
  // a row saved before the id/label split has an id that IS its old label, so it still
  // reads. Restricted to the chosen notice, since a submodel belongs to one.
  const submodelOptions = useMemo(() => {
    // A submodel belongs to one notice, and the ids of two notices' submodels mean
    // nothing side by side. So there is nothing to choose from until a notice is chosen:
    // the dropdown stays disabled rather than listing every notice's colours at once.
    if (!noticeFilter) return [];
    const byId = new Map<string, { id: string; label: string }>();
    const add = (docId: string | null, variant?: string | null, label?: string | null) => {
      const v = (variant || '').trim();
      if (!v || docId !== noticeFilter) return;
      // Through displayVariantLabel, like every other place a variant is SHOWN: the
      // stored label is whatever the client that saved it wrote, and a row written by an
      // older build holds the raw grouping key ("Tissu Beige · Hévéa Foncé"). Idempotent
      // on a label already in this form.
      const shown = displayVariantLabel((label || '').trim() || v);
      const cur = byId.get(v);
      if (!cur) byId.set(v, { id: v, label: shown || v });
      else if (cur.label === v && shown) cur.label = shown;
    };
    for (const it of items) {
      for (const p of it.photos) add(p.docId, p.variant, p.variantLabel);
    }
    for (const n of productNotices) {
      for (const im of n.images) add(n.docId, im.variant, im.variantLabel);
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items, productNotices, noticeFilter]);

  // Both tables filtered to the chosen notice: a part is kept when any of its schematics
  // or photos came from it; a product-photo row when it IS it.
  //
  // Then to the chosen submodel, which filters the PICTURES and not the parts: a part
  // exists in every colour, only its generated render is colour-specific. Hardware, tools
  // and hand-added photos carry no submodel — one render serves them all — so they stay
  // visible under every submodel rather than vanishing from all of them.
  const noticeItems = noticeFilter
    ? items.filter(
        (it) =>
          it.schematics.some((s) => s.docId === noticeFilter) ||
          it.photos.some((p) => p.docId === noticeFilter),
      )
    : items;
  const filteredItems = submodelFilter
    ? noticeItems.map((it) => ({
        ...it,
        photos: it.photos.filter((p) => !p.variant || p.variant === submodelFilter),
      }))
    : noticeItems;
  const noticePhotoRows = noticeFilter
    ? productNotices.filter((n) => n.docId === noticeFilter)
    : productNotices;
  const shownProductNotices = submodelFilter
    ? noticePhotoRows
        .map((n) => ({ ...n, images: n.images.filter((im) => im.variant === submodelFilter) }))
        .filter((n) => n.images.length > 0)
    : noticePhotoRows;

  const shownPhotoCount = shownProductNotices.reduce((n, notice) => n + notice.images.length, 0);

  // A submodel belongs to ONE notice, so a filter that no longer names an available one
  // (the notice changed, or its last photo was deleted) is dropped rather than left to
  // filter everything away silently.
  useEffect(() => {
    if (submodelFilter && !submodelOptions.some((s) => s.id === submodelFilter)) {
      setSubmodelFilter('');
    }
  }, [submodelOptions, submodelFilter]);

  // Remove one product photo from local state after it is deleted server-side.
  const onProductImageDeleted = (id: number) =>
    setProductNotices((prev) =>
      prev
        .map((n) => ({ ...n, images: n.images.filter((im) => im.id !== id) }))
        .filter((n) => n.images.length > 0),
    );

  // Known categories (ontology + any legacy category on an item), for the folders.
  // The ontology's OWN order, not the alphabet's. The server sorts by the position a drag
  // gave a folder and falls back to the name, so re-sorting here would have thrown that
  // away — which is why the categories were alphabetical however they were arranged.
  //
  // Legacy names follow: a category string still only on catalog items has no row to hold
  // a position, so it cannot be placed and goes after everything that can.
  //
  // `catOrder` is the arrangement made a moment ago, applied before the server has been
  // re-read. A drag that waited for a round trip to move the folder is a drag that feels
  // broken, and once the re-read lands the two agree.
  const [catOrder, setCatOrder] = useState<string[] | null>(null);
  const catRank = (name: string) => {
    const at = catOrder?.findIndex((c) => c.toLowerCase() === name.toLowerCase()) ?? -1;
    return at < 0 ? Number.MAX_SAFE_INTEGER : at;
  };
  const categoryOptions = Array.from(
    new Set([...categories.map((c) => c.name), ...items.map((i) => (i.category || '').trim())].filter(Boolean)),
  ).sort((a, b) => catRank(a) - catRank(b));
  // The same names, ordered as the tree reads: each super-category followed by the
  // categories inside it, then the top levels that hold none. A category whose parent
  // does not exist is treated as a top level rather than hidden — it is a folder full of
  // parts, and dropping it from the page to punish a dangling name would lose them.
  const parentOf = (name: string) =>
    categories.find((c) => c.name.toLowerCase() === name.toLowerCase())?.parent?.trim() || '';
  const superNames = new Set(
    categories.map((c) => c.parent?.trim() || '').filter(Boolean).map((p) => p.toLowerCase()),
  );
  const isSuper = (name: string) => superNames.has(name.toLowerCase());
  // What may be chosen as a super-category: anything not already inside one.
  const topLevelNames = categoryOptions.filter((c) => !parentOf(c));
  const orderedCategories = categoryOptions.flatMap((name) => {
    const parent = parentOf(name);
    if (parent && categoryOptions.some((c) => c.toLowerCase() === parent.toLowerCase())) {
      return [];  // drawn under its parent, below
    }
    const children = categoryOptions.filter(
      (c) => parentOf(c).toLowerCase() === name.toLowerCase(),
    );
    return [name, ...children];
  });
  const allGroups = groupByCategory(filteredItems, orderedCategories, t('notice.catalog.uncategorized'));
  // When filtering to a notice, hide the folders that ended up empty (nothing to drag
  // into during a filtered view); unfiltered, keep every folder visible.
  const shownGroups = noticeFilter ? allGroups.filter((g) => g.items.length > 0) : allGroups;
  // Collapsing a super-category hides the categories inside it.
  //
  // Its chevron did nothing before, and it was the most reasonable thing in the world to
  // press: a super-category holds no items of its own, so collapsing it hid the nothing
  // it had and left its children standing exactly where they were. They are siblings in
  // this table, not rows inside its group, so nesting them is this filter rather than
  // anything the group component can do by itself.
  const groups = shownGroups.filter((g) => {
    const parent = g.value ? parentOf(g.value) : '';
    return !parent || !collapsed.has(parent);
  });
  // How many items a category holds across the WHOLE catalog (not the filtered view).
  const categoryItemCount = (name: string) =>
    items.filter((i) => (i.category || '').trim().toLowerCase() === name.toLowerCase()).length;
  // name -> definition, for the folder headers.
  const definitionOf = (name: string) =>
    categories.find((c) => c.name.toLowerCase() === name.toLowerCase())?.definition || '';
  // Localized category label: the French name under the FR toggle (falling back to
  // the English identifier), else the English name.
  const catLabel = (name: string) => localizeCategory(categories, name, language);

  // Assign an item to a category. The catalog PUT replaces the whole row, so
  // resend its current fields; the schematic is left out (the backend keeps the
  // existing one when none is sent).
  const saveCategory = async (item: CatalogItem, category: string) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, category } : i)));
    try {
      await axios.put(`/api/notice/catalog/${item.id}`, {
        kind: item.kind,
        nameEn: item.nameEn,
        nameFr: item.nameFr,
        ref: item.ref,
        category,
      });
    } catch {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, category: item.category } : i)));
    }
  };

  // Drag & drop to move an item between category folders.
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverCat, setDragOverCat] = useState<string | null>(null);
  const dropTo = (categoryValue: string) => {
    const id = draggingId;
    setDraggingId(null);
    setDragOverCat(null);
    if (id == null) return;
    const item = items.find((i) => i.id === id);
    if (!item || (item.category || '').trim() === categoryValue) return;
    void saveCategory(item, categoryValue);
  };

  // The folder being dragged, by its category value.
  const [draggingCat, setDraggingCat] = useState<string | null>(null);

  // A folder dropped on another folder: put it in front of that one.
  //
  // AMONG ITS SIBLINGS ONLY. Dropping Washers on Chair would be asking for something the
  // order cannot express — Chair is inside Components and Washers inside Hardware, and
  // there is no position that puts one in front of the other without also moving it to a
  // different parent. Which parent a folder is in is the pencil's question, and it has a
  // field for it; this answers the one the pencil cannot, which is the order.
  const dropCategory = (targetValue: string) => {
    const moved = draggingCat;
    setDraggingCat(null);
    setDragOverCat(null);
    if (moved == null || moved === targetValue) return;
    if (parentOf(moved).toLowerCase() !== parentOf(targetValue).toLowerCase()) return;

    // Its siblings, in the order they are drawn, with the dragged one lifted out and put
    // back in front of the target.
    const siblings = orderedCategories.filter(
      (c) => parentOf(c).toLowerCase() === parentOf(moved).toLowerCase(),
    );
    const order = siblings.filter((c) => c !== moved);
    const at = order.indexOf(targetValue);
    if (at < 0) return;
    order.splice(at, 0, moved);

    // Every category, with this level's rewritten. The route takes a flat list and
    // numbers it, so the levels have to be sent in one sequence rather than each level
    // numbered from zero against the others — two folders at different depths sharing
    // position 0 would sort by name again, which is what this exists to stop.
    let k = 0;
    const names = orderedCategories.map((c) => (
      parentOf(c).toLowerCase() === parentOf(moved).toLowerCase() ? order[k++] : c
    ));
    setCatOrder(names);
    void axios.post('/api/notice/categories/order', { names })
      .then(() => invalidateCategories())
      .catch(() => {});
  };

  // A row dropped on another row. The same drag the folders already answered, read one
  // level finer: onto a row of ANOTHER folder it still means "file it there", and onto a
  // row of its own it means "put it in front of this one".
  //
  // The catalogue was ordered by creation date, newest first — the order parts happened to
  // be saved in, which is no order at all once a folder holds a dozen and the eye is
  // looking for the screws together.
  const dropOnRow = (targetId: number, categoryValue: string) => {
    const id = draggingId;
    setDraggingId(null);
    setDragOverCat(null);
    if (id == null || id === targetId) return;
    const dragged = items.find((i) => i.id === id);
    if (!dragged) return;
    if ((dragged.category || '').trim() !== categoryValue) {
      void saveCategory(dragged, categoryValue);
      return;
    }

    // The folder's ids in the order they are drawn, with the dragged one lifted out and
    // put back in front of the target.
    const order = items
      .filter((i) => (i.category || '').trim() === categoryValue)
      .map((i) => i.id)
      .filter((x) => x !== id);
    const at = order.indexOf(targetId);
    if (at < 0) return;
    order.splice(at, 0, id);

    // Applied locally first and saved after. A drag that waited for a round trip to move
    // the row is a drag that feels broken; and if the save fails the page still shows what
    // was asked for, which the next reload corrects.
    const byId = new Map(items.map((i) => [i.id, i]));
    let k = 0;
    setItems(items.map((i) => (
      (i.category || '').trim() === categoryValue ? byId.get(order[k++])! : i
    )));
    void axios.post('/api/notice/catalog/order', { ids: order })
      .then(() => invalidateCatalogItems())
      .catch(() => {});
  };

  // Add a new category to the ontology (the one place categories are created).
  const addCategory = async (
    name: string, nameFr: string, definition: string, parent: string,
  ) => {
    const n = name.trim();
    if (!n) return;
    const fr = nameFr.trim();
    await axios.post('/api/notice/categories', { name: n, nameFr: fr, definition, parent });
    invalidateCategories();
    setCategories((prev) =>
      prev.some((c) => c.name.toLowerCase() === n.toLowerCase())
        ? prev
        : [...prev, { name: n, nameFr: fr, definition, parent }],
    );
  };

  // Category being edited in the name modal (null = none).
  const [editingCat, setEditingCat] = useState<Category | null>(null);

  // Save a category's own fields: its English identifier (a rename cascades to items
  // server-side), its French name, and its definition — the one the classifiers read.
  // Mirror the change locally. (Not to be confused with saveCategory above, which puts
  // an ITEM in a category.)
  const editCategory = async (
    oldName: string,
    nameEn: string,
    nameFr: string,
    definition: string,
    parent: string,
  ) => {
    await axios.put(`/api/notice/categories/${encodeURIComponent(oldName)}`, {
      nameEn,
      nameFr,
      definition,
      parent,
    });
    invalidateCategories();
    const lower = oldName.toLowerCase();
    setCategories((prev) =>
      prev.map((c) => (c.name.toLowerCase() === lower
        ? { ...c, name: nameEn, nameFr, definition, parent }
        // A rename cascades to the children's `parent`, exactly as it does server-side,
        // or the folders under it would jump to the top level until the next reload.
        : c.parent.toLowerCase() === lower ? { ...c, parent: nameEn } : c)),
    );
    if (nameEn.toLowerCase() !== lower) {
      setItems((prev) =>
        prev.map((i) =>
          (i.category || '').trim().toLowerCase() === lower ? { ...i, category: nameEn } : i,
        ),
      );
    }
  };

  // The item open in the edit dialog (null = none).
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  // The schematic open in the zoom modal (null = none).
  const [zoomSchematic, setZoomSchematic] = useState<Schematic | null>(null);
  const [zoomPhoto, setZoomPhoto] = useState<Photo | null>(null);
  // The mesh open in the 3D modal (null = none).
  const [openModel, setOpenModel] = useState<Job3D | null>(null);

  // The Parts DB's meshes, fetched once for the whole table and handed out by item.
  // Their own request rather than a join onto /api/notice/catalog: a mesh is not a
  // picture of a part, it lives in another table with its own lifecycle, and a table
  // that renders fine without them should not wait on them to render at all.
  // NULL UNTIL ANSWERED, and a failure said as one — see ProductModelsTable, which spins.
  const [models, setModels] = useState<Job3D[] | null>(null);
  const [modelsFailed, setModelsFailed] = useState(false);
  useEffect(() => {
    void fetchCatalogModels().then(setModels).catch(() => setModelsFailed(true));
  }, []);
  const modelsByItem = useMemo(() => {
    const by = new Map<number, Job3D[]>();
    for (const m of models ?? []) {
      if (m.itemId == null) continue;
      const list = by.get(m.itemId);
      if (list) list.push(m);
      else by.set(m.itemId, [m]);
    }
    return by;
  }, [models]);

  // The images the generator was given as context for one photo, reconstructed from what
  // is stored: the part's schematic FROM THE SAME notice (all its schematics if none
  // matches, e.g. a photo with no recorded notice) plus that notice's product photos. Not
  // the exact bytes of that one run — those are not kept — but the same references, so an
  // already-generated photo still shows its context.
  const photoContext = (photo: Photo): string[] => {
    const item = items.find((it) => it.photos.some((p) => p.id === photo.id));
    const schematics = item?.schematics ?? [];
    const matched = photo.docId ? schematics.filter((s) => s.docId === photo.docId) : [];
    const schem = (matched.length ? matched : schematics).map((s) => schematicSrc(s.id));
    // Only the listing photos ACTUALLY sent as context (the ones the user kept). When that
    // was never recorded (contextIds null, legacy photos), show none rather than every
    // listing photo — the schematic, always sent, is the honest minimum. Restricted to
    // ids the notice still has, so a since-deleted context photo isn't a broken thumbnail.
    const known = new Set(
      (productNotices.find((n) => n.docId === photo.docId)?.images ?? []).map((im) => im.id),
    );
    const prod = (photo.contextIds ?? [])
      .filter((id) => known.has(id))
      .map((id) => listingImageSrc(id));
    return [...schem, ...prod];
  };

  // What ← and → walk from an open picture: the other pictures of the SAME part, in the
  // order its row shows them. A part's photos are what you compare with each other; the
  // next part's are a different question, and the zoom does not say whose part it is.
  const zoomPhotoStrip = zoomPhoto
    ? items.find((it) => it.photos.some((p) => p.id === zoomPhoto.id))?.photos ?? []
    : [];
  const zoomSchematicStrip = zoomSchematic
    ? items.find((it) => it.schematics.some((s) => s.id === zoomSchematic.id))?.schematics ?? []
    : [];

  // Whether the "empty the whole catalog" confirm is open, and whether the wipe is in
  // flight (the request deletes every row, so it is worth a spinner).
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Same, for the product-photos database (its own button and endpoint).
  const [confirmClearPhotos, setConfirmClearPhotos] = useState(false);
  const [clearingPhotos, setClearingPhotos] = useState(false);
  const productPhotoCount = productNotices.reduce((sum, n) => sum + n.images.length, 0);

  // Delete a category from the ontology; its items fall back to Uncategorized.
  const deleteCategory = async (name: string) => {
    const lower = name.toLowerCase();
    await axios.delete(`/api/notice/categories/${encodeURIComponent(name)}`);
    invalidateCategories();
    setCategories((prev) => prev.filter((c) => c.name.toLowerCase() !== lower));
    setItems((prev) =>
      prev.map((i) => ((i.category || '').trim().toLowerCase() === lower ? { ...i, category: '' } : i)),
    );
    setEditingCat(null);
  };

  // Save an item's own fields. The row is replaced whole by the PUT, so send what the
  // dialog holds and keep the fields it does not touch.
  const saveItem = async (it: CatalogItem, fields: CatalogItemFields) => {
    await axios.put(`/api/notice/catalog/${it.id}`, { ...fields, ref: it.ref });
    invalidateCatalogItems();
    setItems((prev) => prev.map((i) => (i.id === it.id ? { ...i, ...fields } : i)));
    setEditingItem(null);
  };

  // Delete an item. The parts tables cache the catalog to answer "already in the
  // database?", and this is the answer changing.
  const deleteItem = async (it: CatalogItem) => {
    await axios.delete(`/api/notice/catalog/${it.id}`);
    invalidateCatalogItems();
    setItems((prev) => prev.filter((i) => i.id !== it.id));
    setEditingItem(null);
  };

  // Empty the catalog: every part goes, the categories stay. The folders remain, now
  // empty, because the ontology is not the parts — it is where they are filed, and this
  // clears the drawer, not the labels on it.
  const clearCatalog = async () => {
    setClearing(true);
    try {
      await axios.delete('/api/notice/catalog');
      invalidateCatalogItems();
      setItems([]);
      setConfirmClear(false);
    } finally {
      setClearing(false);
    }
  };

  // Empty the product-photos database (every notice's stored listing photos).
  const clearPhotos = async () => {
    setClearingPhotos(true);
    try {
      await axios.delete('/api/notice/listing-images');
      setProductNotices([]);
      setConfirmClearPhotos(false);
    } finally {
      setClearingPhotos(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      {/* Only when something sent us here. The catalog reached from the sidebar is
          where you meant to be, and a back arrow there would point at whatever you
          happened to look at before. */}
      {backTo && (
        <Link
          to={backTo}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('notice.catalog.back')}
        </Link>
      )}
      <div className="mb-2 flex items-center justify-center gap-3">
        <Database className="h-6 w-6 text-blue-600" />
        <h1 className="font-sora text-2xl font-bold text-white">
          {t('notice.catalog.title')}
        </h1>
      </div>

      <div className="h-12" />

      {/* Which database. Above the notice filter, because it is the coarser choice: it
          picks the table, the filter narrows whichever one that is. */}
      <SectionNav
        items={[
          // HOW MANY PARTS ARE IN THERE, on the same terms as the photographs beside it:
          // after the notice filter, which sits directly underneath. The submodel filter
          // cannot move this one — a part exists in every colour and only its render is
          // colour-specific — so it holds still while the number next to it changes. That
          // is the difference between the two tables showing, not the counts disagreeing.
          {
            id: 'parts',
            label: `${t('notice.catalog.partsDb')} (${filteredItems.length})`,
          },
          // HOW MANY PICTURES ARE IN THERE, after the filters — which is the number worth
          // reading, since the filters are right underneath and the tab is what you press
          // to go and look. A total that ignored them would disagree with the page it
          // names the moment a notice was chosen.
          {
            id: 'photos',
            label: `${t('notice.catalog.productPhotos')} (${shownPhotoCount})`,
          },
        ]}
        active={db}
        onSelect={(id) => setDb(id as 'parts' | 'photos')}
        // Two cards only: sized to their labels and centered, not stretched across the
        // page into a pair of banners.
      />

      <div className="h-12" />

      {/* Filter both tables down to a single notice (brand-category-product), then to one
          of its submodels. Each is shown as soon as it has something to choose between
          (both default to "all"). The submodel one appears under the notice one: it is
          the finer of the two, and its options are read off the chosen notice. */}
      {noticeOptions.length > 0 && (
        <div className="mx-auto flex w-fit max-w-full flex-col items-stretch gap-2">
          <CenteredSelect
            value={noticeFilter}
            options={[
              { value: '', label: t('notice.catalog.allNotices') },
              ...noticeOptions.map((n) => ({ value: n.docId, label: n.label })),
            ]}
            onChange={setNoticeFilter}
            ariaLabel={t('notice.catalog.filterNotice')}
          />
          <CenteredSelect
            value={submodelFilter}
            options={[
              { value: '', label: t('notice.catalog.allSubmodels') },
              ...submodelOptions.map((s) => ({ value: s.id, label: s.label })),
            ]}
            onChange={setSubmodelFilter}
            ariaLabel={t('notice.catalog.filterSubmodel')}
            // Nothing to pick until a notice is picked — and nothing either when the
            // chosen one has no submodels in the database.
            disabled={submodelOptions.length === 0}
          />
        </div>
      )}

      {noticeOptions.length > 0 && <div className="h-12" />}

      {/* ONE spinner for both databases: they load together, so whichever tab is up
          waits on the same fetch. Then the chosen one, whole — table, its ontology
          control, and its own "empty the database" button. */}
      {loading || !ready ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : db === 'parts' ? (
        <>
        <ProductModelsTable
          models={models}
          failed={modelsFailed}
          notices={productNotices}
          noticeFilter={noticeFilter}
          submodelFilter={submodelFilter}
          linked={linkedProduct}
          onOpen={setOpenModel}
        />
        {groups.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">
            {t('notice.catalog.empty')}
          </p>
        ) : (
          <>
          {/* A CARD, not the page. Every cell in here holds ink chosen for a light surface
              and the table had none of its own — so on the blueprint it was dark text on
              dark blue, readable only where a thumbnail brought its own white with it.
              Same white fill and card border as the app's other tables.

              The table's own outer `border` goes with it: the cells already draw their
              borders (see cellCls), so the card's edge and the table's were two lines
              round the same rectangle. */}
          <div className="overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-sm">
            <table className="w-full border-collapse text-sm">
              {/* Column widths declared rather than inferred, so the table is the same
                  width whatever is open. Without this the columns size to the ROWS: with
                  every folder expanded they grow to fit the thumbnails, and collapsing
                  the lot leaves only the header text to measure, so the whole table
                  snaps narrower — the page appearing to resize itself as a side effect of
                  hiding some rows.

                  The picture columns are pinned (a thumbnail is max-h-20 and its strip is
                  capped at 15rem), the id and the pencil are pinned to what they hold,
                  and what remains goes to the name column, the only one with anything to
                  gain from it. */}
              <colgroup>
                <col className="w-28" />
                {nameCols.map((c) => (
                  <col key={c.label} />
                ))}
                {/* Wide enough for "total length: 2.756"" without wrapping, which is the
                    longest line the cell draws. */}
                <col className="w-44" />
                <col className="w-60" />
                <col className="w-60" />
                <col className="w-60" />
                <col className="w-12" />
              </colgroup>
              <thead>
                <tr>
                  <th className={headCls}>{st('notice.catalog.id')}</th>
                  {nameCols.map((c) => (
                    <th key={c.label} className={headCls}>{c.head}</th>
                  ))}
                  <th className={headCls}>{st('notice.parts.size')}</th>
                  <th className={headCls}>{st('notice.catalog.schematic')}</th>
                  <th className={headCls}>{st('notice.catalog.photosReal')}</th>
                  <th className={headCls}>{st('notice.catalog.models3d')}</th>
                  {/* The pencil column: no heading, because "edit" is not a property of
                      a part. */}
                  <th className={headCls} />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <CatalogCategoryGroup
                    key={g.value || '__uncat__'}
                    group={g}
                    modelsByItem={modelsByItem}
                    collapsed={collapsed.has(g.value)}
                    colCount={colCount}
                    label={g.value ? catLabel(g.value) : g.label}
                    canEditCategory={g.value !== ''}
                    depth={g.value && parentOf(g.value) ? 1 : 0}
                    isParent={g.value !== '' && isSuper(g.value)}
                    childCount={
                      g.value
                        ? categoryOptions.filter(
                            (c) => parentOf(c).toLowerCase() === g.value.toLowerCase(),
                          ).length
                        : 0
                    }
                    dragOver={dragOverCat === g.value}
                    draggingId={draggingId}
                    nameCols={nameCols}
                    cellCls={cellCls}
                    linkedId={linkedId}
                    linkedRef={linkedRef}
                    onToggle={() => toggleCollapsed(g.value)}
                    onEditCategory={() =>
                      setEditingCat({
                        name: g.value,
                        nameFr:
                          categories.find((c) => c.name.toLowerCase() === g.value.toLowerCase())
                            ?.nameFr || '',
                        definition: definitionOf(g.value),
                        parent: parentOf(g.value),
                      })
                    }
                    onCatDragOver={() => setDragOverCat(g.value)}
                    onDropHere={() => dropTo(g.value)}
                    onDropOnRow={(id) => dropOnRow(id, g.value)}
                    draggingCat={draggingCat}
                    onCatDragStart={() => setDraggingCat(g.value)}
                    onCatDragEnd={() => setDraggingCat(null)}
                    onDropCategory={() => dropCategory(g.value)}
                    onRowDragStart={(id) => setDraggingId(id)}
                    onRowDragEnd={() => {
                      setDraggingId(null);
                      setDragOverCat(null);
                    }}
                    onEditItem={(it) => setEditingItem(it)}
                    onZoomSchematic={setZoomSchematic}
                    onZoomPhoto={setZoomPhoto}
                    onOpenModel={setOpenModel}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Per-model tally of the generated real images in the database, UNDER the table
              it counts, in a box of its own: it is a footnote about the table, not another
              row of it. Recomputed from `items`, so it stays live as photos are added or
              removed. The box shrinks to its content (inline-flex) and is centered by the
              row around it. */}
          <div className="mt-4 flex justify-center">
            <div className="inline-flex flex-col items-center gap-1.5 rounded-xl border border-slate-300 bg-slate-50 px-6 py-3 text-sm">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('notice.catalog.dbImagesTitle')}
              </span>
              {GEN_MODELS.map((m) => (
                <span key={m.id} className="flex items-center gap-1.5 text-slate-600">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: m.color }} aria-hidden />
                  {`${m.label} :`}
                  <span className="font-semibold tabular-nums text-slate-900">
                    {modelImageCounts[m.id] ?? 0}
                  </span>
                </span>
              ))}
            </div>
          </div>
          </>
        )}

        {/* Ontology management, under the parts table it acts on: the one place to create
            a category. */}
        <div className="mt-6 flex flex-col items-center gap-1.5">
          <AddCategory
            existing={categoryOptions}
            parents={topLevelNames}
            onAdd={addCategory}
            t={t}
          />
        </div>

        {/* Empty the parts catalog — its OWN button, under its OWN table. Only when there
            is something to empty. The categories are untouched. */}
        {items.length > 0 && (
          <div className="mt-6 flex justify-center">
            <ClearDbButton label={t('notice.catalog.emptyDb')} onClick={() => setConfirmClear(true)} />
          </div>
        )}
        </>
      ) : (
        <>
        {/* Product photos harvested from each notice's web listing, grouped by notice
            (filtered to the chosen notice). */}
        <NoticeProductImages
          notices={shownProductNotices}
          onDeleted={onProductImageDeleted}
        />

        {/* Empty the product-photos database — its OWN button, under its OWN table. */}
        {productPhotoCount > 0 && (
          <div className="mt-6 flex justify-center">
            <ClearDbButton label={t('notice.catalog.emptyDb')} onClick={() => setConfirmClearPhotos(true)} />
          </div>
        )}
        </>
      )}

      {confirmClear && (
        <ConfirmDialog
          title={t('notice.catalog.emptyDbConfirm')}
          body={t('notice.catalog.emptyDbBody').replace('{n}', String(items.length))}
          confirmLabel={clearing ? t('notice.catalog.emptyingDb') : t('notice.catalog.emptyDb')}
          cancelLabel={t('notice.catalog.cancel')}
          onConfirm={() => void clearCatalog()}
          onCancel={() => setConfirmClear(false)}
        />
      )}

      {confirmClearPhotos && (
        <ConfirmDialog
          title={t('notice.catalog.emptyPhotosConfirm')}
          body={t('notice.catalog.emptyPhotosBody').replace('{n}', String(productPhotoCount))}
          confirmLabel={clearingPhotos ? t('notice.catalog.emptyingDb') : t('notice.catalog.emptyDb')}
          cancelLabel={t('notice.catalog.cancel')}
          onConfirm={() => void clearPhotos()}
          onCancel={() => setConfirmClearPhotos(false)}
        />
      )}

      {editingCat && (
        <CategoryFormModal
          title={t('notice.catalog.editCategory')}
          nameEn={editingCat.name}
          nameFr={editingCat.nameFr}
          definition={editingCat.definition}
          parent={editingCat.parent}
          // Only categories that could hold this one: not itself, and not one already
          // inside something, since the ontology is two levels and not a tree.
          parents={topLevelNames.filter((c) => c !== editingCat.name)}
          taken={categoryOptions.filter((c) => c !== editingCat.name)}
          onSave={(en, fr, def, par) => editCategory(editingCat.name, en, fr, def, par)}
          onDelete={() => deleteCategory(editingCat.name)}
          // What deleting costs, said before it happens: the items do not go with it.
          // Counted off ALL items, not the notice-filtered view — deleting the category
          // frees every item in it, filter or no filter.
          deleteConfirm={
            categoryItemCount(editingCat.name) > 0
              ? t('notice.catalog.deleteCategoryItems').replace(
                  '{n}',
                  String(categoryItemCount(editingCat.name)),
                )
              : t('notice.catalog.deleteCategoryConfirm')
          }
          onClose={() => setEditingCat(null)}
          t={t}
        />
      )}

      {editingItem && (
        <CatalogItemModal
          title={t('notice.catalog.editItem')}
          initial={{
            kind: editingItem.kind,
            nameEn: editingItem.nameEn,
            nameFr: editingItem.nameFr,
            category: editingItem.category,
          }}
          onSave={(fields) => saveItem(editingItem, fields)}
          onDelete={() => deleteItem(editingItem)}
          onClose={() => setEditingItem(null)}
        />
      )}

      {zoomSchematic && (
        <CatalogImageModal
          src={schematicSrc(zoomSchematic.id)}
          id={zoomSchematic.id}
          siblings={zoomSchematicStrip.map((s) => s.id)}
          onNavigate={(id) => {
            const s = zoomSchematicStrip.find((x) => x.id === id);
            if (s) setZoomSchematic(s);
          }}
          deleteUrl={`/api/notice/catalog/schematic/${zoomSchematic.id}`}
          deleteLabel={t('notice.catalog.deleteSchematic')}
          onDeleted={() => {
            setZoomSchematic(null);
            // The catalog changed — drop it from the row, and let the parts tables
            // that read the catalog to answer "already saved?" re-read it.
            setItems((prev) =>
              prev.map((i) => ({
                ...i,
                schematics: i.schematics.filter((s) => s.id !== zoomSchematic.id),
              })),
            );
            invalidateCatalogItems();
          }}
          onClose={() => setZoomSchematic(null)}
        />
      )}

      {zoomPhoto && (
        <CatalogImageModal
          src={photoSrc(zoomPhoto.id)}
          id={zoomPhoto.id}
          siblings={zoomPhotoStrip.map((p) => p.id)}
          onNavigate={(id) => {
            const p = zoomPhotoStrip.find((x) => x.id === id);
            if (p) setZoomPhoto(p);
          }}
          modelLabel={IS_DEMO ? null : GEN_MODELS.find((m) => m.id === zoomPhoto.model)?.label ?? zoomPhoto.model}
          runSeq={zoomPhoto.runSeq}
          runFromDev={zoomPhoto.runFromDev}
          runMode={zoomPhoto.runMode}
          view={zoomPhoto.view}
          context={photoContext(zoomPhoto)}
          deleteUrl={`/api/notice/catalog/image/${zoomPhoto.id}`}
          deleteLabel={t('notice.catalog.deletePhoto')}
          asDbToggle
          onDeleted={() => {
            setZoomPhoto(null);
            setItems((prev) =>
              prev.map((i) => ({ ...i, photos: i.photos.filter((p) => p.id !== zoomPhoto.id) })),
            );
            // Phase 2 derives its toggles from the catalog, so deleting a photo here needs no
            // bookkeeping — Phase 2 re-reads the catalog and the toggle flips off on its own.
            invalidateCatalogItems();
          }}
          onClose={() => setZoomPhoto(null)}
        />
      )}

      {openModel && (
        <MeshModal
          // Versioned by the edit count, because a repair overwrites the file at this
          // address: without it the Parts DB would keep showing the mesh from before the
          // correction, which is the one place that must not.
          src={meshSrc(openModel.id, meshStamp(openModel))}
          caption={IS_DEMO ? `#${openModel.id}` : `#${openModel.id} · ${openModel.model}`}
          // The size of the PART this mesh is a model of. Without it the viewer labelled
          // all three axes unknown here, while the same mesh opened from the reader was
          // labelled correctly — the size was in the notice's state and the database had
          // no column for it.
          dims={items.find((i) => i.id === openModel.itemId)?.size ?? null}
          trueScale={isTrueScale(openModel)}
          onClose={() => setOpenModel(null)}
        />
      )}
    </div>
  );
}

// The "Vider la base" button, shared by the two databases (parts, product photos) so
// they clear the same way. The caller opens the confirm dialog.
function ClearDbButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:border-rose-400 hover:text-rose-600"
    >
      <Trash2 className="h-4 w-4" />
      {label}
    </button>
  );
}
